import { Select } from "@mantine/core";
import { useStore } from "../store";
import {
  REASONING_LEVELS,
  describeReasoningEffort,
  reasoningLabel,
} from "./reasoningEffort";

/**
 * Picks how hard the model should think.
 *
 * Deliberately offers the full ladder regardless of the selected model. The model that ends up
 * serving a template is not knowable here: `llm` may be empty (routing/tenant default decides), it
 * may be a routing alias that redirects, and fallback candidates can have different accepted ranges.
 * Restricting the options to the primary model's accepted range would therefore be wrong in
 * exactly the cases where routing does something interesting. The backend clamps a level
 * outside that range to whatever the served model accepts rather than failing, so the honest
 * UI is to annotate, not restrict.
 */
export default function ReasoningEffortSelect({ value, onChange, llm, ...props }) {
  const providers = useStore((state) => state.providers);
  const described = describeReasoningEffort(value, llm, providers);

  let description = "How hard the model should think. Empty = provider default.";

  if (described?.state === "ignored") {
    description = `${llm} has no reasoning control — this will be ignored.`;
  } else if (described?.state === "clamped") {
    description = `${llm} tops out at ${described.effectiveLabel} — this will be clamped, not rejected.`;
  } else if (described?.state === "raised") {
    description = `${llm} starts at ${described.effectiveLabel} — this will be raised, not rejected.`;
  } else if (described?.state === "unknownModel" && llm) {
    description =
      "The serving model is resolved at run time; levels outside its range are clamped to what it accepts.";
  }

  return (
    <Select
      label="Reasoning"
      description={description}
      placeholder="Provider default"
      clearable
      value={value ?? null}
      onChange={onChange}
      data={REASONING_LEVELS.map((level) => ({
        value: level,
        label: reasoningLabel(level),
      }))}
      {...props}
    />
  );
}
